import React, { useState, useRef, useMemo } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import PlannerIcons from '../../components/shared/PlannerIcons';
import LargePreviewModal from '../../components/ui/LargePreviewModal';

// Internal Sub-component for individual color swatches
const ColorSwatch = ({ color, onRemove }) => (
    <div className="relative group cursor-pointer" title={color}>
        <div 
            className="w-12 h-12 rounded-2xl shadow-md border-2 border-white relative z-10 hover:z-20 hover:scale-110 transition-all ring-1 ring-black/5" 
            style={{ backgroundColor: color }}
        ></div>
        <button 
            onClick={onRemove} 
            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-30 border-2 border-white"
        >
            ✕
        </button>
        <p className="text-[9px] font-mono text-gray-400 mt-1.5 text-center font-bold tracking-tighter uppercase">{color}</p>
    </div>
);

export default function ProjectDesign({ project, updateProject, user, storage, safeNavigate }) {
  const [activeTab, setActiveTab] = useState('inspiration'); // 'inspiration' | 'palettes'
  const [newItemType, setNewItemType] = useState('upload'); // 'upload', 'url', 'group'
  const [newImgUrl, setNewImgUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Layout & State Management
  const [targetGroupId, setTargetGroupId] = useState('');
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [uploadingGroups, setUploadingGroups] = useState({});
  const [newPaletteName, setNewPaletteName] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [taggingItemId, setTaggingItemId] = useState(null);
  const [rentalForm, setRentalForm] = useState({ name: '', url: '', price: '' });

  // Filtering Logic
  const existingGroups = useMemo(() => 
    (project.designBoard || []).filter(item => item.type === 'image-group'),
    [project.designBoard]
  );
  
  const activeExpandedGroup = project.designBoard?.find(i => i.id === expandedGroupId);

  // --- Handlers ---

  const saveRentalTag = (e) => {
      e.preventDefault();
      const updatedBoard = (project.designBoard || []).map(item => 
          item.id === taggingItemId ? { ...item, rental: rentalForm.name ? { ...rentalForm } : null } : item
      );
      updateProject(project.id, { designBoard: updatedBoard });
      setTaggingItemId(null);
      setRentalForm({ name: '', url: '', price: '' });
  };

  const addUrlImage = (e) => {
    e.preventDefault();
    if(!newImgUrl) return;
    
    const newItem = { id: Date.now().toString(), type: 'image', url: newImgUrl, caption: caption };
    if (targetGroupId) {
        const updatedBoard = project.designBoard.map(item => {
            if (item.id === targetGroupId) {
                return { ...item, images: [...(item.images || []), newImgUrl] };
            }
            return item;
        });
        updateProject(project.id, { designBoard: updatedBoard });
    } else {
        updateProject(project.id, { designBoard: [newItem, ...(project.designBoard || [])] });
    }
    
    setNewImgUrl('');
    setCaption('');
    setTargetGroupId('');
  };

  const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file || !storage || !user) return;
      setIsUploading(true);
      try {
          const storagePath = `planner-projects/${project.id}/moodboard/${Date.now()}_${file.name}`;
          const fileRef = ref(storage, storagePath);
          await uploadBytes(fileRef, file);
          const url = await getDownloadURL(fileRef);
          
          if (targetGroupId) {
              const updatedBoard = project.designBoard.map(item => {
                  if (item.id === targetGroupId) {
                      return { ...item, images: [...(item.images || []), url] };
                  }
                  return item;
              });
              updateProject(project.id, { designBoard: updatedBoard });
          } else {
              const newItem = { id: Date.now().toString(), type: 'image', url: url, caption: caption };
              updateProject(project.id, { designBoard: [newItem, ...(project.designBoard || [])] });
          }
          
          setCaption('');
          setTargetGroupId('');
      } catch (err) {
          console.error("Upload failed", err);
          alert("Failed to upload image.");
      } finally {
          setIsUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
      }
  };

  const addGroup = (e) => {
      e.preventDefault();
      if(!newGroupTitle) return;
      const newGroup = { id: Date.now().toString(), type: 'image-group', title: newGroupTitle, images: [] };
      updateProject(project.id, { designBoard: [newGroup, ...(project.designBoard || [])] });
      setNewGroupTitle('');
  };

  const addPalette = (e) => {
    e.preventDefault();
    if(!newPaletteName) return;
    const newPalette = { id: Date.now().toString(), name: newPaletteName, colors: [] };
    updateProject(project.id, { colorPalettes: [...(project.colorPalettes || []), newPalette] });
    setNewPaletteName('');
  };

  const addColorToPalette = (paletteId, colorVal) => {
    const updated = (project.colorPalettes || []).map(p => {
        if(p.id === paletteId && !p.colors.includes(colorVal)) {
            return { ...p, colors: [...p.colors, colorVal] };
        }
        return p;
    });
    updateProject(project.id, { colorPalettes: updated });
  };

  const handleDragStart = (e, itemId) => {
      e.dataTransfer.setData('itemId', itemId);
  };

  const handleDropIntoGroup = (e, groupId) => {
      e.preventDefault();
      const itemId = e.dataTransfer.getData('itemId');
      if (!itemId || itemId === groupId) return;

      const board = project.designBoard || [];
      const draggedItem = board.find(i => i.id === itemId);

      if (!draggedItem || draggedItem.type !== 'image') return;

      const updatedBoard = board.reduce((acc, item) => {
          if (item.id === itemId) return acc;
          if (item.id === groupId) {
              acc.push({ ...item, images: [...(item.images || []), draggedItem.url] });
              return acc;
          }
          acc.push(item);
          return acc;
      }, []);

      updateProject(project.id, { designBoard: updatedBoard });
  };

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      {/* Lightbox Preview */}
      {previewImageUrl && (
          <LargePreviewModal 
              imageUrl={previewImageUrl} 
              onClose={() => setPreviewImageUrl(null)} 
          />
      )}

      {/* Tagging Modal */}
      {taggingItemId && (
           <div className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setTaggingItemId(null)}>
               <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-sm p-8" onClick={e => e.stopPropagation()}>
                   <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                       <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 font-serif"><span>🏷️</span> Tag Rental Item</h3>
                       <button onClick={() => setTaggingItemId(null)} className="text-gray-400 hover:text-red-500 transition text-2xl leading-none">&times;</button>
                   </div>
                   <form onSubmit={saveRentalTag} className="space-y-5">
                       <div>
                           <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Item Name</label>
                           <input type="text" value={rentalForm.name} onChange={e => setRentalForm({...rentalForm, name: e.target.value})} placeholder="e.g. Gold Chiavari Chair" className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" required />
                       </div>
                       <div>
                           <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Price per unit</label>
                           <div className="relative">
                               <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                               <input type="number" step="0.01" min="0" value={rentalForm.price} onChange={e => setRentalForm({...rentalForm, price: e.target.value})} placeholder="0.00" className="w-full pl-8 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" required />
                           </div>
                       </div>
                       <div>
                           <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Link to Marketplace Item</label>
                           <input type="url" value={rentalForm.url} onChange={e => setRentalForm({...rentalForm, url: e.target.value})} placeholder="https://host-it.com/listings/..." className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" required />
                       </div>
                       <div className="pt-4 flex gap-3">
                           <button type="button" onClick={() => { setRentalForm({name:'', url:'', price:''}); }} className="flex-1 py-3.5 bg-gray-100 text-gray-600 rounded-2xl font-black text-sm hover:bg-gray-200 transition">Clear</button>
                           <button type="submit" className="flex-1 py-3.5 bg-gray-900 text-white rounded-2xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95">Save Tag</button>
                       </div>
                   </form>
               </div>
           </div>
       )}

      {/* Expanded Group Modal */}
      {activeExpandedGroup && (
           <div className="fixed inset-0 z-[400] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setExpandedGroupId(null)}>
               <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                   <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white z-10 shadow-sm flex-shrink-0">
                       <div>
                           <h3 className="text-2xl font-bold text-gray-900 font-serif">{activeExpandedGroup.title}</h3>
                           <p className="text-sm text-gray-500 font-medium">{activeExpandedGroup.images?.length || 0} items in this collection</p>
                       </div>
                       <button onClick={() => setExpandedGroupId(null)} className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full transition font-bold text-xl">&times;</button>
                   </div>
                   <div className="p-6 overflow-y-auto flex-grow custom-scrollbar bg-gray-50/50">
                       <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
                           {(activeExpandedGroup.images || []).map((imgUrl, idx) => (
                               <div key={idx} className="relative group/img break-inside-avoid shadow-md rounded-xl overflow-hidden bg-gray-200">
                                   <img 
                                       src={imgUrl} 
                                       className="w-full h-auto object-cover cursor-zoom-in" 
                                       alt="Group item" 
                                       onClick={() => setPreviewImageUrl(imgUrl)}
                                   />
                                   <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                                        <button onClick={(e) => { 
                                            e.stopPropagation(); 
                                            const updatedBoard = project.designBoard.map(item => {
                                                if (item.id === activeExpandedGroup.id) {
                                                    const newImages = [...item.images];
                                                    newImages.splice(idx, 1);
                                                    return { ...item, images: newImages };
                                                }
                                                return item;
                                            });
                                            updateProject(project.id, { designBoard: updatedBoard });
                                        }} className="bg-white/90 backdrop-blur-md text-red-600 p-2 rounded-full hover:scale-110 hover:bg-white transition-transform shadow-lg">
                                            <PlannerIcons.Trash />
                                        </button>
                                   </div>
                               </div>
                           ))}
                       </div>
                   </div>
                   <div className="p-6 border-t border-gray-100 bg-white z-10 flex justify-between items-center shadow-inner flex-shrink-0">
                       <button onClick={() => setExpandedGroupId(null)} className="px-8 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition shadow-md w-full sm:w-auto">Close Collection</button>
                   </div>
               </div>
           </div>
       )}

      {/* Header & Tool Switcher */}
      <div className="bg-white p-6 rounded-[2rem] border border-gray-200 shadow-sm flex flex-col lg:flex-row gap-8 justify-between items-center">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-gradient-to-br from-pink-50 to-orange-50 text-pink-500 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-pink-100/50">
            🎨
          </div>
          <div>
            <h3 className="font-serif text-3xl font-bold text-gray-900 leading-tight">Vision Board</h3>
            <p className="text-sm text-gray-500 font-medium">Curate the aesthetic and visual language of your event.</p>
          </div>
        </div>
        
        <div className="flex bg-gray-100 p-1.5 rounded-2xl border border-gray-200 shadow-inner w-full lg:w-auto">
            <button 
                onClick={() => setActiveTab('inspiration')}
                className={`flex-1 lg:px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === 'inspiration' ? 'bg-white text-pink-600 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}
            >
                Inspiration
            </button>
            <button 
                onClick={() => setActiveTab('palettes')}
                className={`flex-1 lg:px-8 py-2.5 rounded-xl text-sm font-black transition-all ${activeTab === 'palettes' ? 'bg-white text-pink-600 shadow-md' : 'text-gray-500 hover:text-gray-900'}`}
            >
                Palettes
            </button>
        </div>
      </div>

      {activeTab === 'inspiration' ? (
          <div className="space-y-8 animate-fade-in">
              {/* Control Card */}
              <div className="bg-white p-6 rounded-[2rem] border border-gray-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 via-orange-400 to-pink-500"></div>
                <div className="flex flex-wrap gap-4 mb-6">
                    {['upload', 'url', 'group'].map(type => (
                        <button 
                            key={type}
                            onClick={() => setNewItemType(type)}
                            className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all border ${newItemType === type ? 'bg-gray-900 text-white border-gray-900 shadow-md' : 'bg-white text-gray-500 border-gray-200 hover:border-pink-300'}`}
                        >
                            {type}
                        </button>
                    ))}
                </div>

                <div className="flex flex-col md:flex-row gap-4 items-end">
                    {newItemType !== 'group' && (
                        <div className="flex-1 w-full space-y-4">
                            {existingGroups.length > 0 && (
                                <div>
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block ml-1">Move to Collection</label>
                                    <select value={targetGroupId} onChange={e => setTargetGroupId(e.target.value)} className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-pink-500 outline-none transition appearance-none">
                                        <option value="">Standalone Inspiration</option>
                                        {existingGroups.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                                    </select>
                                </div>
                            )}
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block ml-1">Caption / Note</label>
                                <input type="text" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Modern floral arch with pampas..." className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition" />
                            </div>
                        </div>
                    )}

                    {newItemType === 'upload' && (
                        <div className="w-full md:w-auto flex flex-col gap-2">
                            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept="image/*" />
                            <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 h-[52px]">
                                {isUploading ? 'Uploading...' : 'Choose & Add Inspiration'}
                            </button>
                        </div>
                    )}

                    {newItemType === 'url' && (
                        <form onSubmit={addUrlImage} className="w-full flex flex-col sm:flex-row gap-3">
                            <input type="url" value={newImgUrl} onChange={e => setNewImgUrl(e.target.value)} placeholder="Paste high-res image URL..." className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition h-[52px]" required />
                            <button type="submit" className="bg-gray-900 text-white px-8 py-3 rounded-2xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95 h-[52px]">Add URL</button>
                        </form>
                    )}

                    {newItemType === 'group' && (
                        <form onSubmit={addGroup} className="w-full flex flex-col sm:flex-row gap-3">
                            <input type="text" value={newGroupTitle} onChange={e => setNewGroupTitle(e.target.value)} placeholder="Collection Name (e.g. Mood & Texture)" className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-pink-500 outline-none transition h-[52px]" required />
                            <button type="submit" className="bg-gray-900 text-white px-8 py-3 rounded-2xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95 h-[52px]">Create Collection</button>
                        </form>
                    )}
                </div>
              </div>

              {/* Masonry Mood Board */}
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
                {(project.designBoard || []).map(item => {
                  if (item.type === 'image-group') {
                      const displayImages = item.images?.slice(0, 3) || [];
                      const extraCount = (item.images?.length || 0) - 3;
                      return (
                        <div 
                            key={item.id} 
                            onClick={() => setExpandedGroupId(item.id)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleDropIntoGroup(e, item.id)}
                            className="break-inside-avoid bg-white rounded-3xl p-5 shadow-sm border border-gray-100 hover:shadow-xl hover:border-pink-200 transition-all cursor-pointer group flex flex-col gap-4 relative overflow-hidden"
                        >
                            <div className="flex justify-between items-center relative z-10">
                                <h4 className="font-serif text-xl font-bold text-gray-900 truncate pr-4">{item.title}</h4>
                                <span className="text-[10px] font-black text-pink-600 bg-pink-50 px-2 py-1 rounded-full uppercase shrink-0">{item.images?.length || 0} ITEMS</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 relative z-10">
                                {displayImages.map((url, idx) => (
                                    <div key={idx} className={`relative overflow-hidden rounded-xl bg-gray-100 ${idx === 0 ? 'row-span-2 h-full' : 'aspect-square'}`}>
                                        <img src={url} className="w-full h-full object-cover" alt="Group" />
                                        {idx === 2 && extraCount > 0 && (
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white backdrop-blur-[2px]">
                                                <span className="font-black text-lg">+{extraCount}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {(!item.images || item.images.length === 0) && (
                                    <div className="col-span-full h-32 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center text-gray-400 text-xs font-bold uppercase tracking-widest">
                                        Empty Collection
                                    </div>
                                )}
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); updateProject(project.id, { designBoard: project.designBoard.filter(i => i.id !== item.id) }); }} className="absolute -top-10 -right-10 group-hover:top-3 group-hover:right-3 p-2 bg-white/90 backdrop-blur-md text-red-500 rounded-full shadow-lg border border-red-50 transition-all z-20"><PlannerIcons.Trash /></button>
                        </div>
                      );
                  }
                  
                  return (
                    <div 
                        key={item.id} 
                        draggable
                        onDragStart={(e) => handleDragStart(e, item.id)}
                        className="break-inside-avoid relative group rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl border border-gray-200 bg-white flex flex-col transition-all duration-500 cursor-grab active:cursor-grabbing"
                    >
                        <img 
                            src={item.url} 
                            className="w-full h-auto object-cover block cursor-zoom-in group-hover:scale-[1.03] transition-transform duration-700" 
                            alt="Inspo" 
                            onClick={() => setPreviewImageUrl(item.url)}
                        />
                        
                        {item.caption && (
                            <div className="p-4 bg-white border-t border-gray-50">
                                <p className="text-xs text-gray-600 font-medium leading-relaxed italic">"{item.caption}"</p>
                            </div>
                        )}
                        
                        {/* Click-to-Rent Overlay */}
                        {item.rental && (
                            <a href={item.rental.url} target="_blank" rel="noopener noreferrer" className="block p-4 bg-pink-50/50 hover:bg-pink-100 border-t border-pink-100 transition-colors group/rental" onMouseDown={e => e.stopPropagation()}>
                                <div className="flex justify-between items-center">
                                    <div className="min-w-0 pr-3">
                                        <span className="text-[9px] font-black text-pink-400 uppercase tracking-widest block mb-0.5">Matched Rental</span>
                                        <h5 className="text-xs font-black text-pink-900 truncate flex items-center gap-1.5">
                                            <span className="text-lg">🛒</span> {item.rental.name}
                                        </h5>
                                    </div>
                                    <span className="text-sm font-black text-pink-700 shrink-0 bg-white px-2 py-1 rounded-lg shadow-sm border border-pink-100">${parseFloat(item.rental.price).toFixed(2)}</span>
                                </div>
                            </a>
                        )}

                        {/* Hover Actions */}
                        <div className="absolute top-4 right-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0 duration-300">
                            <button 
                                onClick={() => updateProject(project.id, { designBoard: project.designBoard.filter(i => i.id !== item.id) })}
                                className="bg-white/90 backdrop-blur-md text-red-500 p-2.5 rounded-full shadow-xl border border-red-50 hover:bg-red-500 hover:text-white transition-all transform active:scale-90"
                            >
                                <PlannerIcons.Trash />
                            </button>
                            <button 
                                onClick={() => { setTaggingItemId(item.id); setRentalForm(item.rental || {name:'', url:'', price:''}); }}
                                className="bg-white/90 backdrop-blur-md text-blue-600 p-2.5 rounded-full shadow-xl border border-blue-50 hover:bg-blue-500 hover:text-white transition-all transform active:scale-90"
                                title="Tag Rental Product"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.998 0 013 12V7a4 4 0 014-4z" /></svg>
                            </button>
                        </div>
                    </div>
                  );
                })}
              </div>

              {(!project.designBoard || project.designBoard.length === 0) && (
                  <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-[3rem] bg-gray-50/50">
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm text-4xl grayscale opacity-50">🖼️</div>
                      <h4 className="font-serif text-xl font-bold text-gray-900">Your design board is a blank canvas.</h4>
                      <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">Upload inspiration images, create collections, and tag rentals to build your story.</p>
                  </div>
              )}
          </div>
      ) : (
          <div className="space-y-8 animate-fade-in">
              {/* Palette Controls */}
              <div className="bg-white p-6 rounded-[2rem] border border-gray-200 shadow-sm">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
                      <div className="flex-1">
                          <h4 className="font-serif text-2xl font-bold text-gray-900">Color Systems</h4>
                          <p className="text-sm text-gray-500 font-medium">Define your palettes for attire, florals, and stationery.</p>
                      </div>
                      <form onSubmit={addPalette} className="flex gap-2 w-full sm:w-auto">
                          <input type="text" value={newPaletteName} onChange={e => setNewPaletteName(e.target.value)} placeholder="Palette group name..." className="flex-grow p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-pink-500 outline-none transition" required />
                          <button type="submit" className="bg-gray-900 text-white px-6 py-3 rounded-xl font-black text-sm hover:bg-black transition shadow-lg">+ Add Palette</button>
                      </form>
                  </div>
              </div>

              {/* Palette Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {(project.colorPalettes || []).map(palette => (
                      <div key={palette.id} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
                          <div className="absolute top-0 left-0 w-full h-1 bg-gray-900 opacity-5"></div>
                          <div className="flex justify-between items-center mb-6">
                              <h5 className="font-serif text-xl font-bold text-gray-800">{palette.name}</h5>
                              <button 
                                onClick={() => updateProject(project.id, { colorPalettes: project.colorPalettes.filter(p => p.id !== palette.id) })} 
                                className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                              >
                                  <PlannerIcons.Trash />
                              </button>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 items-center">
                              {/* Swatches */}
                              <div className="flex gap-3 flex-wrap">
                                  {palette.colors.map((color, idx) => (
                                      <ColorSwatch 
                                          key={idx} 
                                          color={color} 
                                          onRemove={() => {
                                              const updated = project.colorPalettes.map(p => 
                                                  p.id === palette.id ? { ...p, colors: p.colors.filter((_, i) => i !== idx) } : p
                                              );
                                              updateProject(project.id, { colorPalettes: updated });
                                          }} 
                                      />
                                  ))}
                              </div>
                              
                              {/* Add Swatch Tool */}
                              <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-[1.5rem] p-1.5 shadow-inner hover:bg-gray-100 transition-colors ml-auto md:ml-0">
                                  <input 
                                      type="color" 
                                      id={`picker-${palette.id}`} 
                                      defaultValue="#ec4899" 
                                      className="w-10 h-10 rounded-2xl cursor-pointer border-0 p-0 bg-transparent overflow-hidden" 
                                  />
                                  <button 
                                      onClick={() => {
                                          const val = document.getElementById(`picker-${palette.id}`).value;
                                          addColorToPalette(palette.id, val);
                                      }}
                                      className="text-xs font-black uppercase tracking-widest text-gray-600 hover:text-pink-600 pr-4 pl-1"
                                  >
                                      Add
                                  </button>
                              </div>
                          </div>
                      </div>
                  ))}
                  {(!project.colorPalettes || project.colorPalettes.length === 0) && (
                      <div className="col-span-full py-20 text-center border-2 border-dashed border-gray-200 rounded-[3rem] bg-gray-50/50">
                          <div className="text-5xl mb-4 grayscale opacity-40">🎨</div>
                          <h4 className="font-serif text-xl font-bold text-gray-900">Define your color language.</h4>
                          <p className="text-sm text-gray-500 mt-1">Create palettes to ensure consistency across florals, attire, and decor.</p>
                      </div>
                  )}
              </div>
          </div>
      )}
    </div>
  );
}